export interface ChatWindowFields {
  lastCustomerInteraction?: string | Date | null;
  lastMessageFromMe?: boolean | null;
  lastMessageTimestamp?: string | Date | null;
  remoteJid?: string | null;
}

/**
 * Determines whether a chat's 24-hour WhatsApp messaging window has expired.
 * 
 * Rules:
 * 1. Web chats and non-WhatsApp chats do not have WhatsApp Meta 24-hour window restrictions.
 * 2. If the customer has sent a message, the window expires 24 hours (86,400,000 ms) after the interaction.
 * 3. If there has never been an inbound customer interaction, the window is expired / not open.
 * 4. When a new customer message arrives (or the window is reset), this returns false (active).
 */
export function isMessagingWindowExpired(chat: ChatWindowFields | null | undefined): boolean {
  if (!chat || !chat.remoteJid) return false;

  const remoteJid = String(chat.remoteJid);

  // Web chats, Facebook Messenger chats, Instagram chats, and internal chats do not have WhatsApp 24h template restrictions
  if (
    remoteJid.endsWith('@web.chat') ||
    remoteJid.startsWith('web_') ||
    remoteJid.endsWith('@facebook.messenger') ||
    remoteJid.startsWith('fb_') ||
    remoteJid.endsWith('@instagram.messenger') ||
    remoteJid.endsWith('@instagram.direct') ||
    remoteJid.startsWith('ig_')
  ) {
    return false;
  }

  // Find the best (most recent) indicator of last customer interaction.
  // We check both `lastCustomerInteraction` and `lastMessageTimestamp` (when the
  // last message is from the customer) and use whichever is more recent.
  // This guards against stale data when real-time updates don't carry every field.
  let interactionTime: number | null = null;

  if (chat.lastCustomerInteraction) {
    const t = new Date(chat.lastCustomerInteraction).getTime();
    if (!isNaN(t)) interactionTime = t;
  }

  // Also consider lastMessageTimestamp if the last message is from the customer
  if (!chat.lastMessageFromMe && chat.lastMessageTimestamp) {
    const t = new Date(chat.lastMessageTimestamp).getTime();
    if (!isNaN(t) && (interactionTime === null || t > interactionTime)) {
      interactionTime = t;
    }
  }

  if (interactionTime === null) {
    // No inbound customer interaction recorded -> 24h service window has expired / not open
    return true;
  }

  const elapsed = Date.now() - interactionTime;
  const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

  return elapsed > TWENTY_FOUR_HOURS_MS;
}
